import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'weight', 'weightUnit', 'bodyFat', 'activity', 'goal', 'training',
    'proteinLow', 'proteinHigh', 'perMeal', 'perKg',
    'breakfast', 'lunch', 'dinner', 'snack', 'insight'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    let weight = parseFloat(this.weightTarget.value) || 70
    const unit = this.weightUnitTarget.value
    const bodyFat = parseFloat(this.bodyFatTarget.value) || 0
    const activity = this.activityTarget.value
    const goal = this.goalTarget.value
    const training = this.trainingTarget.value

    // Convert to kg if needed
    if (unit === 'lbs') {
      weight /= 2.205
    }

    // Calculate lean body mass if body fat provided
    const leanMass = bodyFat > 0 ? weight * (1 - bodyFat / 100) : weight

    // Base protein multipliers (g/kg)
    const multipliers = {
      maintain: { low: 1.2, high: 1.6 },
      build: { low: 1.6, high: 2.2 },
      lose: { low: 1.8, high: 2.4 },
      athletic: { low: 1.6, high: 2.2 },
      elderly: { low: 1.2, high: 1.6 }
    }

    let { low, high } = multipliers[goal] || multipliers.maintain

    // Adjust for activity level
    const activityBonus = {
      sedentary: -0.1,
      light: 0,
      moderate: 0.1,
      active: 0.2,
      athlete: 0.3
    }
    low += activityBonus[activity] || 0
    high += activityBonus[activity] || 0

    // Adjust for training experience (advanced lifters may need less per kg)
    if (training === 'beginner' && goal === 'build') {
      high += 0.2
    }

    // Use lean mass if available, otherwise total weight
    const massToUse = bodyFat > 0 ? leanMass : weight

    const proteinLow = Math.round(massToUse * low)
    const proteinHigh = Math.round(massToUse * high)
    const proteinMid = Math.round((proteinLow + proteinHigh) / 2)

    this.proteinLowTarget.textContent = proteinLow
    this.proteinHighTarget.textContent = proteinHigh
    this.perKgTarget.textContent = `${low.toFixed(1)}-${high.toFixed(1)}`

    // Calculate per meal (assuming 4 meals)
    const perMealLow = Math.round(proteinLow / 4)
    const perMealHigh = Math.round(proteinHigh / 4)
    this.perMealTarget.textContent = `${perMealLow}-${perMealHigh}`

    // Distribution
    const distribution = this.getDistribution(proteinMid)
    this.breakfastTarget.textContent = `${distribution.breakfast}g`
    this.lunchTarget.textContent = `${distribution.lunch}g`
    this.dinnerTarget.textContent = `${distribution.dinner}g`
    this.snackTarget.textContent = `${distribution.snack}g`

    // Insight
    this.insightTarget.textContent = this.getInsight(goal, activity, training)
  }

  getDistribution(totalProtein) {
    // Slight emphasis on post-workout/evening for muscle building
    return {
      breakfast: Math.round(totalProtein * 0.25),
      lunch: Math.round(totalProtein * 0.28),
      dinner: Math.round(totalProtein * 0.28),
      snack: Math.round(totalProtein * 0.19)
    }
  }

  getInsight(goal, _activity, _training) {
    const insights = {
      build: 'For muscle building, aim for 25-40g of protein per meal to maximize muscle protein synthesis. ' +
        'Spreading intake across 4 meals optimizes absorption.',
      lose: 'Higher protein during fat loss helps preserve muscle mass. ' +
        'Prioritize protein at each meal and consider casein before bed.',
      maintain: 'Your protein needs are moderate. Focus on complete protein sources and consistent daily intake.',
      athletic: 'Athletes benefit from protein timing around training. Consider 20-30g within 2 hours of exercise.',
      elderly: 'Older adults have higher protein needs per meal (30-40g) to overcome anabolic resistance. ' +
        'Leucine-rich sources are especially important.'
    }
    return insights[goal] || insights.maintain
  }
}
